// Utilidades de cómputo de rendimiento de cargas de combustible.
// El rendimiento se calcula por diferencia de odómetro entre una carga y la
// siguiente del MISMO camión, dividida entre los litros de la carga inicial.

/**
 * Agrupa por camión (proyectoId), ordena por odómetro y calcula, por carga:
 *  - kmRecorridos = odómetro(siguiente) − odómetro(actual)   (null si no hay siguiente o delta < 0)
 *  - rendimiento  = kmRecorridos / litros                     (null si no aplica)
 */
export function computarRendimientoPorCamion(cargas) {
  const porCamion = {};
  (cargas || []).forEach((c) => {
    const key = (c.proyectoId || "").toString();
    if (!porCamion[key]) porCamion[key] = [];
    porCamion[key].push(c);
  });

  const resultado = [];
  Object.values(porCamion).forEach((lista) => {
    const ordenadas = [...lista].sort((a, b) => (a.kmOdometro || 0) - (b.kmOdometro || 0));
    ordenadas.forEach((c, i) => {
      const sig = ordenadas[i + 1];
      let kmRecorridos = null;
      let rendimiento = null;
      if (sig) {
        const delta = (sig.kmOdometro || 0) - (c.kmOdometro || 0);
        if (delta >= 0) {
          kmRecorridos = delta;
          rendimiento = c.litros > 0 ? delta / c.litros : null;
        }
      }
      resultado.push({ ...c, kmRecorridos, rendimiento });
    });
  });
  return resultado;
}

export function resumenGlobal(cargasComp) {
  const totalLitros = cargasComp.reduce((s, c) => s + (c.litros || 0), 0);
  const gastoTotal = cargasComp.reduce((s, c) => s + (c.total || 0), 0);
  const kms = cargasComp.map((c) => c.kmOdometro || 0);
  const totalKm = kms.length > 1 ? Math.max(...kms) - Math.min(...kms) : 0;
  const litrosConTramo = cargasComp
    .filter((c) => c.rendimiento != null)
    .reduce((s, c) => s + (c.litros || 0), 0);
  const kmPorLitro = litrosConTramo > 0 ? totalKm / litrosConTramo : 0;
  const costoPorKm = totalKm > 0 ? gastoTotal / totalKm : 0;
  const rends = cargasComp.filter((c) => c.rendimiento != null).map((c) => c.rendimiento);
  const avgRendimiento = rends.length > 0 ? rends.reduce((a, b) => a + b, 0) / rends.length : 0;
  return { totalLitros, gastoTotal, totalKm, kmPorLitro, costoPorKm, avgRendimiento };
}

export function resumenPorCamion(cargasComp) {
  const map = {};
  cargasComp.forEach((c) => {
    const key = (c.proyectoId || "").toString();
    if (!map[key]) map[key] = { proyectoId: key, nombre: c.proyectoNombre || "Camión", cargas: [] };
    map[key].cargas.push(c);
  });
  return Object.values(map)
    .map((g) => {
      const r = resumenGlobal(g.cargas);
      return {
        proyectoId: g.proyectoId,
        nombre: g.nombre,
        numCargas: g.cargas.length,
        ...r,
      };
    })
    .sort((a, b) => b.kmPorLitro - a.kmPorLitro);
}

/**
 * Resumen por chofer (km/L ponderado = km del tramo / litros del tramo).
 * Solo cuentan las cargas con rendimiento 
 */
export function resumenPorChofer(cargasComp) {
  const map = {};
  cargasComp.forEach((c) => {
    if (c.rendimiento == null) return;
    const key = c.choferNombre || "Sin chofer";
    if (!map[key]) map[key] = { chofer: key, km: 0, litros: 0, numTramos: 0 };
    map[key].km += c.kmRecorridos || 0;
    map[key].litros += c.litros || 0;
    map[key].numTramos += 1;
  });
  return Object.values(map)
    .map((v) => ({
      ...v,
      rendimiento: v.litros > 0 ? +(v.km / v.litros).toFixed(3) : 0,
    }))
    .sort((a, b) => b.rendimiento - a.rendimiento);
}

/**
 * Detecta cargas cuyo rendimiento se desvía más de `umbralPct`% del promedio
 */
export function detectarAnomalias(cargasComp, umbralPct = 25) {
  const baseline = {};
  const porCamion = {};
  cargasComp.forEach((c) => {
    const key = (c.proyectoId || "").toString();
    if (!porCamion[key]) porCamion[key] = [];
    porCamion[key].push(c);
  });
  Object.entries(porCamion).forEach(([key, lista]) => {
    const r = resumenGlobal(lista);
    baseline[key] = r.kmPorLitro;
  });

  const anomalias = [];
  cargasComp.forEach((c) => {
    if (c.rendimiento == null) return;
    const key = (c.proyectoId || "").toString();
    const base = baseline[key] || 0;
    if (base <= 0) return;
    const desviacionPct = ((c.rendimiento - base) / base) * 100;
    if (Math.abs(desviacionPct) >= umbralPct) {
      anomalias.push({
        ...c,
        baseline: base,
        desviacionPct: +desviacionPct.toFixed(1),
        tipo: desviacionPct < 0 ? "Bajo" : "Alto",
      });
    }
  });
  return anomalias.sort((a, b) => Math.abs(b.desviacionPct) - Math.abs(a.desviacionPct));
}
